import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { IoCloseSharp } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslation } from "react-i18next";
import CreateModal from "../../../component/Model/CreateModel";
import {
  useAddRoutesSchedulesMutation,
  useGetRoutesSchedulesDetailsQuery,
  useUpdateRoutesSchedulesMutation,
} from "../../../store/api/vendor/routesSchedulesSlice";
import { capitalizeString } from "../../../utils/dateAndTimeConversion";
import { AddressAutocomplete } from "../../auth/parent-registration/SignUp";
import AddRouteScheduleSkeleton from "../../../component/skelton/AddRouteScheduleSkeleton";

const initialState = {
  route_name: "",
  pickup_address: "",
  dropoff_address: "",
  pickup_lat: "",
  pickup_lng: "",
  dropoff_lat: "",
  dropoff_lng: "",
  radius_meters: "",
};

const AddRouteSchedule = () => {
  const { t } = useTranslation();
  const { id, role } = useParams();
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  // Add Routes Schedule
  const [createRouteSchedule, { isLoading }] = useAddRoutesSchedulesMutation();

  // Update Route Schedule
  const [updateRouteSchedule, { isLoading: isUpdateLoading }] =
    useUpdateRoutesSchedulesMutation();

  // Get route schedule details for update
  const {
    data: RouteDetailData,
    isLoading: isDataLoading,
    isSuccess: isSuccessRouteDetail,
  } = useGetRoutesSchedulesDetailsQuery(id, {
    skip: !id,
  });

  // Handle Change Input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // ${t("errors." + name)}
    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  // Parent Address Autocomplete
  const handleParentAddressSelect = (name, address) => {
    console.log(address);

    const addrs =
      name === "pickup_address"
        ? {
            pickup_lat: address.latitude,
            pickup_lng: address.longitude,
            pickup_address: address.street,
          }
        : {
            dropoff_lat: address.latitude,
            dropoff_lng: address.longitude,
            dropoff_address: address.street,
          };

    setFormData((prev) => ({
      ...prev,
      ...addrs,
    }));
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (!value || (typeof value === "string" && value.trim() === "")) {
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();
    try {
      let response;

      if (id) {
        response = await updateRouteSchedule({ data: formData, id }).unwrap();
      } else {
        response = await createRouteSchedule(formData).unwrap();
      }
      if (response.statusCode === 200 || response.statusCode === 201) {
        setShow(true);
      }
    } catch (err) {
      const message =
        err?.data?.message || err.message || "Something went wrong!";
      toast.error(message);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData(initialState);
    setErrors({});
  };

  useEffect(() => {
    if (isSuccessRouteDetail && RouteDetailData?.data) {
      setFormData({
        route_name: RouteDetailData?.data?.route_name || "",
        radius_meters: RouteDetailData?.data?.radius_meters || "",
        pickup_address: RouteDetailData?.data?.pickup_address || "",
        dropoff_address: RouteDetailData?.data?.dropoff_address || "",
        pickup_lat: RouteDetailData?.data?.pickup_lat || "",
        pickup_lng: RouteDetailData?.data?.pickup_lng || "",
        dropoff_lat: RouteDetailData?.data?.dropoff_lat || "",
        dropoff_lng: RouteDetailData?.data?.dropoff_lng || "",
      });
    }
  }, [RouteDetailData, isSuccessRouteDetail]);

  const handleClose = () => {
    setShow(false);
    navigate(`/route-and-schedule`);
  };

  return (
    <>
      <div className="outerBox">
        <div className="d-flex mb-3">
          <button className="page-back-btn" onClick={() => navigate(-1)}>
            ← {t("back")}
          </button>
        </div>
        <div className="dbSubHd mb-4">
          {`${t("route")} ${t("and")} ${t("schedule")} ${t("info")}`}
        </div>

        {isDataLoading ? (
          // Skeleton Loading State
          <AddRouteScheduleSkeleton />
        ) : (
          <>
            <ul className="form d-flex flex-wrap p-0 pb-5">
              <li className="col-12 col-md-6">
                <label className="form-label fw-semibold">
                  {`${t("route")} ${t("name")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="route_name"
                  placeholder={`${t("route")} ${t("name")}`}
                  value={formData.route_name}
                  onChange={handleChange}
                />
                {errors?.route_name && (
                  <span className="text-danger small">{errors.route_name}</span>
                )}
              </li>
              <li className="col-12 col-md-6">
                <label className="form-label fw-semibold">
                  {t("radius")} (m) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="formInput"
                  name="radius_meters"
                  placeholder={`${t("route")} ${t("radius")}`}
                  value={formData.radius_meters}
                  onChange={handleChange}
                />
                {errors?.radius_meters && (
                  <span className="text-danger small">
                    {errors.radius_meters}
                  </span>
                )}
              </li>

              <li className="col-12 ">
                <label className="form-label fw-semibold">
                  {t("start")} {t("address")}
                  <span className="text-danger">*</span>
                </label>
                <AddressAutocomplete
                  value={formData.pickup_address}
                  onChange={handleChange}
                  onSelect={(addr) =>
                    handleParentAddressSelect("pickup_address", addr)
                  }
                  name="pickup_address"
                  t={t}
                />
                {errors?.pickup_address && (
                  <span className="text-danger small">
                    {errors.pickup_address}
                  </span>
                )}
              </li>
              <li className="col-12 ">
                <label className="form-label fw-semibold">
                  {t("end")} {t("address")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <AddressAutocomplete
                  value={formData.dropoff_address}
                  onChange={handleChange}
                  onSelect={(addr) =>
                    handleParentAddressSelect("dropoff_address", addr)
                  }
                  name="dropoff_address"
                  t={t}
                />
                {errors?.dropoff_address && (
                  <span className="text-danger small">
                    {errors.dropoff_address}
                  </span>
                )}
              </li>
            </ul>

            {/* Buttons */}
            <div className="pt-4 mt-3 border-top text-end">
              <Button
                variant="outline-secondary"
                onClick={handleReset}
                className="me-3 px-4 py-2"
              >
                {t("btns.reset")}
              </Button>

              <Button
                variant="primary"
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || isUpdateLoading}
                className="px-4 py-2"
              >
                {isLoading || isUpdateLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">{t("loading")}...</span>
                    </span>
                  </>
                ) : (
                  t("btns.submit")
                )}
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal show={show}>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <CreateModal
          title={`${t("route")} ${t("and")} ${t("schedule")} ${
            id ? t("updated") : t("added")
          } ${t("successfully")}!`}
          text={
            id
              ? `${t("route")} ${t("and")} ${t("schedule")} ${t("have")} ${t(
                  "been",
                )} ${t("updated")}.`
              : `${t("route")} ${t("and")} ${t("schedule")} ${t("have")} ${t(
                  "been",
                )} ${t("added")}.`
          }
          onClose={handleClose}
        />
      </Modal>

      {/* styles */}
      <style jsx>{`
        .address,
        .address:focus {
          padding: 10px 12px;
          background-color: rgb(255, 255, 255);
          color: rgb(73, 80, 87);
          font-size: 14px;
          width: 100%;
          height: 55px;
          border: 1px solid #d6d6d6;
          border-radius: 6px;
          font-weight: 500;
          box-shadow: none;
        }
      `}</style>
    </>
  );
};

export default AddRouteSchedule;
